'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MagnifyingGlass, Plus, CaretDown, CaretLeft, CaretRight, DotsSixVertical } from '@phosphor-icons/react';
import { useWorkflowStore } from '../../stores/workflowStore';
import { NODE_META, NODE_PALETTE_CATEGORIES } from './nodeTypes';

interface NodePanelProps {
  className?: string;
}

export function NodePanel({ className = '' }: NodePanelProps) {
  const { nodes: storeNodes, edges, addNode, addEdge } = useWorkflowStore();
  const [search, setSearch] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);

  /** Node types that have no top (target) handle — they can't receive connections. */
  const INPUT_TYPES  = new Set(['load_excel', 'load_csv', 'load_api']);
  /** Node types that have no bottom (source) handle — they can't be a tail to connect from. */
  const OUTPUT_TYPES = new Set(['export_excel', 'export_csv']);

  /**
   * Return the single connectable tail node:
   * - no outgoing edges, AND
   * - not an output-only node (no source handle)
   */
  const getSingleTail = () => {
    if (storeNodes.length === 0) return null;
    const sources = new Set(edges.map((e) => e.source));
    const sinks = storeNodes.filter(
      (n) => !sources.has(n.id) && !OUTPUT_TYPES.has(n.type ?? '')
    );
    return sinks.length === 1 ? sinks[0] : null;
  };

  const handleAddNode = (type: string) => {
    const meta = NODE_META[type];
    if (!meta) return;

    const tail = getSingleTail();

    const id = `${type}_${Date.now()}`;
    const position = tail
      ? { x: tail.position.x, y: tail.position.y + 150 }
      : { x: 80 + storeNodes.length * 40, y: 80 + storeNodes.length * 40 };

    addNode({
      id,
      type,
      position,
      data: {
        label: meta.label,
        config: { type, params: {} },
        status: 'idle' as const,
      },
    });

    // Only auto-wire if the new node can accept an incoming connection
    if (tail && !INPUT_TYPES.has(type)) {
      addEdge({ id: `e_${tail.id}_${id}`, source: tail.id, target: id });
    }
  };

  // ── Collapsed sidebar ────────────────────────────────────────────────────
  if (isPanelCollapsed) {
    return (
      <div className={`w-10 flex-shrink-0 flex flex-col bg-white border-r border-zinc-200 overflow-hidden ${className}`}>
        <button
          onClick={() => setIsPanelCollapsed(false)}
          className="w-full flex items-center justify-center h-9 border-b border-zinc-100 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50 transition-colors flex-shrink-0"
          title="展开节点库 (Alt+[)"
        >
          <CaretRight className="w-3.5 h-3.5" weight="bold" />
        </button>

        <div className="flex flex-col items-center gap-1.5 py-2 px-1.5 overflow-y-auto">
          {NODE_PALETTE_CATEGORIES.map((cat) => {
            const firstType = cat.types[0];
            const meta = firstType ? NODE_META[firstType] : null;
            if (!meta) return null;
            const Icon = meta.icon;
            return (
              <div
                key={cat.label}
                className={`w-7 h-7 rounded-lg ${meta.bg} ${meta.border} border flex items-center justify-center cursor-pointer hover:brightness-90 transition-all`}
                title={cat.label}
                onClick={() => setIsPanelCollapsed(false)}
              >
                <Icon className={`w-3.5 h-3.5 ${meta.color}`} weight="fill" />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const q = search.toLowerCase();
  const filteredCategories = NODE_PALETTE_CATEGORIES.map((cat) => ({
    ...cat,
    types: cat.types.filter((t) => {
      if (!q) return true;
      const m = NODE_META[t];
      return m && (m.label.toLowerCase().includes(q) || m.desc.toLowerCase().includes(q));
    }),
  })).filter((cat) => cat.types.length > 0);

  const toggleCollapse = (label: string) =>
    setCollapsedCategories((c) => ({ ...c, [label]: !c[label] }));

  return (
    <div className={`w-52 flex-shrink-0 flex flex-col bg-white border-r border-zinc-200 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-zinc-100 flex-shrink-0 flex items-center justify-between">
        <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">节点库</p>
        <button
          onClick={() => setIsPanelCollapsed(true)}
          className="p-0.5 text-zinc-300 hover:text-zinc-600 hover:bg-zinc-100 rounded transition-colors"
          title="折叠节点库 (Alt+[)"
        >
          <CaretLeft className="w-3 h-3" weight="bold" />
        </button>
      </div>

      {/* Search */}
      <div className="px-2 py-2 border-b border-zinc-100 flex-shrink-0">
        <div className="relative">
          <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索节点..."
            className="w-full pl-7 pr-2.5 py-1.5 text-xs bg-zinc-50 border border-zinc-200 rounded-lg
              focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400
              placeholder:text-zinc-400"
          />
        </div>
      </div>

      {/* Category list */}
      <div className="flex-1 overflow-y-auto">
        {filteredCategories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
            <p className="text-xs text-zinc-400">未找到匹配节点</p>
          </div>
        ) : (
          filteredCategories.map((cat) => {
            const isCollapsed = collapsedCategories[cat.label];
            return (
              <div key={cat.label}>
                {/* Category header */}
                <button
                  onClick={() => toggleCollapse(cat.label)}
                  className="w-full flex items-center justify-between px-3 py-1.5
                    text-[10px] font-semibold text-zinc-400 uppercase tracking-widest
                    hover:bg-zinc-50 transition-colors"
                >
                  {cat.label}
                  <motion.div animate={{ rotate: isCollapsed ? 0 : 180 }} transition={{ duration: 0.18 }}>
                    <CaretDown className="w-3 h-3" weight="bold" />
                  </motion.div>
                </button>

                {/* Node chips */}
                <AnimatePresence initial={false}>
                  {!isCollapsed && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      className="overflow-hidden"
                    >
                      <div className="px-1.5 pb-2 space-y-0.5">
                        {cat.types.map((type) => {
                          const meta = NODE_META[type];
                          if (!meta) return null;
                          const Icon = meta.icon;
                          return (
                            <div
                              key={type}
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.setData('application/workflow-node-type', type);
                                e.dataTransfer.effectAllowed = 'copy';
                              }}
                              className={`
                                group flex items-center gap-2 px-2 py-1.5 rounded-lg border
                                cursor-grab active:cursor-grabbing select-none
                                ${meta.bg} ${meta.border}
                                hover:brightness-95 transition-all duration-100
                              `}
                              title={meta.desc}
                            >
                              {/* Drag handle hint */}
                              <DotsSixVertical
                                className="w-2.5 h-2.5 text-zinc-300 group-hover:text-zinc-400 flex-shrink-0 -ml-0.5"
                                weight="bold"
                              />

                              <Icon className={`w-3.5 h-3.5 ${meta.color} flex-shrink-0`} weight="fill" />

                              <div className="flex-1 min-w-0">
                                <p className={`text-[11px] font-semibold ${meta.color} truncate leading-tight`}>
                                  {meta.label}
                                </p>
                                <p className="text-[9px] text-zinc-400 truncate leading-tight">{meta.desc}</p>
                              </div>

                              {/* Quick-add button (shown on hover) */}
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddNode(type);
                                }}
                                className={`
                                  w-5 h-5 rounded flex items-center justify-center flex-shrink-0
                                  opacity-0 group-hover:opacity-100 transition-opacity
                                  ${meta.bg} hover:brightness-90
                                `}
                                title={`添加 ${meta.label}`}
                              >
                                <Plus className={`w-3 h-3 ${meta.color}`} weight="bold" />
                              </motion.button>
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })
        )}
      </div>

      {/* Footer tip */}
      <div className="px-3 py-2 border-t border-zinc-100 flex-shrink-0">
        <p className="text-[9px] text-zinc-400 text-center leading-relaxed">
          拖拽到画布 · 或悬停后点击 <span className="font-bold">+</span> 快速添加
        </p>
      </div>
    </div>
  );
}
